import { store } from '../store.js';

/**
 * Rotation composable — lineup, libero, substitutions, roster management.
 * Bridge pattern: receives component instance during Options API phase.
 */
export function useRotation(instance) {
  function getBackRowMiddlePosition() {
    const middlePositions = instance.currentLineup
      .filter(item => item.player && item.player.role === 'middle')
      .map(item => item.rotationPosition);
    return middlePositions.find(position => [1, 5, 6].includes(position)) || null;
  }

  function applyLiberoSubstitution(lineupNumbers) {
    if (!Array.isArray(lineupNumbers) || lineupNumbers.length < 6) {
      return lineupNumbers;
    }
    if (!instance.currentPoint.currentLiberoNumber || !instance.startingPlayersConfirmed) {
      return [...lineupNumbers];
    }
    const backRowMiddlePosition = getBackRowMiddlePosition();
    if (!backRowMiddlePosition) {
      return [...lineupNumbers];
    }
    const isServing = instance.currentPoint.startPoint === 'Serve';
    if (isServing && backRowMiddlePosition === 1) {
      return [...lineupNumbers];
    }
    const nextPositions = [...lineupNumbers];
    nextPositions[backRowMiddlePosition - 1] = instance.currentPoint.currentLiberoNumber;
    return nextPositions;
  }

  function updateStartingPlayers(players, rotationId, libero) {
    const sortedPlayers = players.slice().sort((a, b) => a.rotationPosition - b.rotationPosition);
    instance.currentLineup = sortedPlayers;

    instance.currentPoint.currentRotationCode = rotationId;

    instance.currentPoint.currentLineupNumbers = instance.currentLineup.map(player => player.player.number);
    instance.currentPoint.currentOnCourtNumbers = applyLiberoSubstitution(instance.currentPoint.currentLineupNumbers);
    instance.currentPoint.currentLiberoNumber = libero ? libero.number : null;

    instance.startingPlayersConfirmed = true;
    instance.startPointDisabled = false;
  }

  function updateMatchRoster({ roster, metadata }) {
    instance.matchRoster = roster;
    instance.matchLiberos = roster.filter(player => player && player.playerType === 'libero');
    store.setMatchRoster(roster);
    if (metadata) {
      store.setMatchMetadata(metadata);
    }
    instance.matchReady = true;
    instance.showRosterSetup = false;
  }

  function resetToRosterSelection() {
    if (instance.currentSetNumber > 1) {
      return;
    }
    instance.startingPlayersConfirmed = false;
    instance.showRosterSetup = true;
  }

  function handlePlayerSubstitution({ substitutedPlayer, newPlayer }) {
    if (instance.$refs.serveAction) {
      instance.$refs.serveAction.updateSelectedPlayer(substitutedPlayer, newPlayer);
    }

    if (substitutedPlayer && substitutedPlayer.role && newPlayer) {
      newPlayer.role = substitutedPlayer.role;
    }

    instance.currentLineup = store.currentLineup.slice().sort((a, b) => a.rotationPosition - b.rotationPosition);

    const positionIndex = instance.currentPoint.currentLineupNumbers.findIndex(p => p === substitutedPlayer.number);
    if (positionIndex !== -1) {
      instance.currentPoint.currentLineupNumbers[positionIndex] = newPlayer.number;
    }
    instance.currentPoint.currentOnCourtNumbers = applyLiberoSubstitution(instance.currentPoint.currentLineupNumbers);
  }

  function handleLiberoSwapBack() {
    if (!instance.currentPoint.currentLiberoNumber) {
      return;
    }
    const onCourt = instance.currentPoint.currentOnCourtNumbers || [];
    const base = instance.currentPoint.currentLineupNumbers || [];
    const liberoIndex = onCourt.findIndex(number => number === instance.currentPoint.currentLiberoNumber);
    if (liberoIndex === -1 || !base[liberoIndex]) {
      return;
    }
    const nextPositions = [...onCourt];
    nextPositions[liberoIndex] = base[liberoIndex];
    instance.currentPoint.currentOnCourtNumbers = nextPositions;
  }

  function handleLiberoSubIn({ positionId }) {
    if (!instance.currentPoint.currentLiberoNumber || !positionId) {
      return;
    }
    const onCourt = instance.currentPoint.currentOnCourtNumbers || [];
    const nextPositions = [...onCourt];
    nextPositions[positionId - 1] = instance.currentPoint.currentLiberoNumber;
    instance.currentPoint.currentOnCourtNumbers = nextPositions;
  }

  function handleLiberoSwap({ substitutedPlayer, newLibero }) {
    if (!substitutedPlayer || !newLibero) {
      return;
    }
    const onCourt = instance.currentPoint.currentOnCourtNumbers || [];
    const liberoIndex = onCourt.findIndex(number => number === substitutedPlayer.number);
    if (liberoIndex === -1) {
      return;
    }
    const nextPositions = [...onCourt];
    nextPositions[liberoIndex] = newLibero.number;
    instance.currentPoint.currentOnCourtNumbers = nextPositions;
    instance.currentPoint.currentLiberoNumber = newLibero.number;
    store.currentLibero = newLibero;
  }

  return {
    getBackRowMiddlePosition,
    applyLiberoSubstitution,
    updateStartingPlayers,
    updateMatchRoster,
    resetToRosterSelection,
    handlePlayerSubstitution,
    handleLiberoSwapBack,
    handleLiberoSubIn,
    handleLiberoSwap
  };
}
